import React from "react";
import { Box } from "@mui/material";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

// The impact AI answers are markdown, and the same renderer the rest of the app
// uses renders them — react-markdown with remarkGfm, which is what turns the
// pipe rows and task lists an answer routinely contains into real tables and
// lists instead of a paragraph of punctuation.
//
// What this wrapper adds is FIT. The answers lead with `##` headings, and a
// browser-default h2 inside a 640px drawer or a small popover is larger than the
// drawer's own title. Everything below is scale, not decoration: headings are
// pulled down to the surrounding type, the vertical rhythm is tightened, and the
// first and last blocks lose their outer margins so the text sits flush in
// whatever box it is dropped into.
const markdownSx = {
    color: "text.primary",
    fontSize: "0.875rem",
    lineHeight: 1.6,
    wordBreak: "break-word",
    "& > :first-of-type": { mt: 0 },
    "& > :last-child": { mb: 0 },
    "& h1, & h2, & h3, & h4": {
        fontSize: "0.9375rem",
        fontWeight: 700,
        color: "#00438E",
        mt: 2,
        mb: 0.75,
    },
    "& p": { my: 1 },
    "& ul, & ol": { my: 1, pl: 2.5 },
    "& li": { mb: 0.5 },
    "& li > p": { my: 0 },
    "& strong": { fontWeight: 700 },
    "& code": {
        backgroundColor: "rgba(0,0,0,0.06)",
        borderRadius: 0.5,
        px: 0.5,
        fontSize: "0.8125rem",
    },
    "& pre": {
        backgroundColor: "rgba(0,0,0,0.06)",
        borderRadius: 1,
        p: 1.25,
        overflowX: "auto",
        "& code": { backgroundColor: "transparent", px: 0 },
    },
    // An answer's table must not widen the drawer — it scrolls inside its own box.
    "& table": { width: "100%", borderCollapse: "collapse", my: 1, fontSize: "0.8125rem" },
    "& th, & td": { border: "1px solid", borderColor: "divider", p: 0.75, textAlign: "left" },
    "& th": { backgroundColor: "rgba(0,67,142,0.06)", fontWeight: 700 },
    "& blockquote": {
        my: 1,
        ml: 0,
        pl: 1.5,
        borderLeft: "3px solid",
        borderColor: "divider",
        color: "text.secondary",
    },
    "& a": { color: "#00438E" },
};

export default function AiMarkdown({ children, sx }) {
    if (!children) return null;
    return (
        <Box sx={{ ...markdownSx, ...sx }}>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{children}</ReactMarkdown>
        </Box>
    );
}
